import {
    CrossRegionPersistence,
    DataFetchingRouter,
    GlobalStyles,
    SiteLoader,
} from "@brightagency/headstart-common";
import { detect } from "detect-browser";
import NProgress from "nprogress";
import React, { useEffect } from "react";
import { render } from "react-dom";
import "what-input";
import { QueryClientProvider, QueryClient } from "react-query";
import { Normalize } from "styled-normalize";
import { TitleUpdater } from "./components/atoms/TitleUpdater";
import { GoogleAnalytics } from "./components/molecules/GoogleAnalytics";
import { ErrorBoundary } from "./components/organisms/ErrorBoundary";
import { RegionGlobalStyles } from "./components/organisms/NProgressStyles";
import { NotFoundBoundary } from "./components/templates/NotFound";
import { PageBase } from "./components/templates/PageBase";
import { siteData, zIndex } from "./constants";
import { Routes } from "./constants/routes";
import { SortModes } from "./constants/types";
import { useThemeColors } from "./controllers";
import { AppStateContainer, useAppState, useBaseUrl, useSortMode } from "./services/app-state";
import { hasScrollToElementInHash, scrollToElementId } from "./utils";
import { fetchJSON } from "./utils/ajax";
import { doesSuburbMatchCurrentUrl } from "./utils/basic-only";
import { useSortingCoords } from "./utils/hooks";

// Configure the site loading bar
NProgress.configure({
    minimum: 0.3,
    trickleSpeed: 250,
    easing: "ease",
    speed: 250,
    barSelector: ".bar",
    template:
        '<div class="bar"><div class="peg"></div></div><div class="spinner"><div class="spinner-icon"></div></div>',
});

export const App: React.FC = () => {
    const [isInitialLoadFinished, setisInitialLoadFinished] = React.useState(false);
    const [store, actions] = useAppState();
    const sortingCoords = useSortingCoords();
    const baseUrl = useBaseUrl();
    const [sortMode] = useSortMode();
    const { openOutsideAuPopup, openLegacyBrowserPopup } = actions;
    const { setThemeColor } = useThemeColors();

    useEffect(() => {
        if (sortingCoords && sortMode === SortModes.DistanceFromUser) {
            const { lat, lng } = sortingCoords;
            fetchJSON<boolean>(
                `${baseUrl}/wp-json/headstart/v1/in-australia?lat=${lat}&lng=${lng}`,
            ).then(isInAustralia => {
                if (isInAustralia === false) {
                    openOutsideAuPopup();
                }
            });
        }
    }, [baseUrl, openOutsideAuPopup, sortMode, sortingCoords]);

    useEffect(() => {
        const browser = detect();
        if (browser?.type === "browser" && browser.name === "ie") {
            openLegacyBrowserPopup();
        }
    }, [openLegacyBrowserPopup]);

    const handleLoadingStateChange = (data: {
        loading: boolean;
        initial?: boolean;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error?: any;
    }) => {
        if (data.loading) {
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }, 100);
            NProgress.start();
        } else {
            NProgress.done();
            if (!isInitialLoadFinished) {
                setTimeout(() => {
                    setisInitialLoadFinished(true);
                }, 100);
            }
            setTimeout(() => {
                if (hasScrollToElementInHash(window.location.hash)) {
                    
                    scrollToElementId(window.location.hash);
                }
            }, 300);
        }
        if (data.error) {
            NProgress.done();
        }
    };

    const queryClient = React.useMemo(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        staleTime: 1000 * 10, // 10s
                        refetchOnWindowFocus: false,
                    },
                },
            }),
        [],
    );

    useEffect(() => {
        setThemeColor(colors => {
            if (isInitialLoadFinished) {
                return colors.default;
            } else {
                return colors.loading;
            }
        });
    }, [isInitialLoadFinished, setThemeColor]);

    return (
        <>
            <SiteLoader isVisible={!isInitialLoadFinished} zIndex={zIndex.loader} />
            <Normalize />
            <GlobalStyles />
            <RegionGlobalStyles />
            <QueryClientProvider client={queryClient}>
                <AppStateContainer isGlobal globalData={{ site: siteData, acf: siteData.acf,regions_mapping: siteData.regions_mapping }}>
                    <DataFetchingRouter
                        basename={store.global.site.installPath}
                        routes={Routes}
                        onLoadingStateChange={handleLoadingStateChange}
                        passToPreload={{ store, actions }}
                        initialPreload={async () => {
                            const promises = Promise.all([
                                actions.getTaxonomyData(),
                                actions.getPostTypeData(),
                            ]);
                            await promises;
                        }}
                    >
                        {route => (
                            <>
                                <GoogleAnalytics />
                                <TitleUpdater />
                                <PageBase>
                                    <NotFoundBoundary>
                                        <ErrorBoundary>{route}</ErrorBoundary>
                                    </NotFoundBoundary>
                                </PageBase>
                            </>
                        )}
                    </DataFetchingRouter>
                </AppStateContainer>
            </QueryClientProvider>
        </>
    );
};

window.addEventListener("DOMContentLoaded", () => {
    // const url = new URL(window.location.href);
    // const preventRedirect =
    //     url.searchParams.get("redirect") === "false" ||
    //     url.searchParams.get("select-region") === "true";

    // if (preventRedirect) {
    //     CrossRegionPersistence.clearChosenSuburb();
    // }

    const suburb = CrossRegionPersistence.getChosenSuburb();
    if (suburb && !doesSuburbMatchCurrentUrl(suburb)) {
        CrossRegionPersistence.clearChosenSuburb();
    }

    render(<App />, document.getElementById("app"));
});